import { Record } from 'immutable';
import { ApiDonorSemenFile, DonorSemenFile } from './DonorSemenFile';

export type ApiDonorSemen = {
  collected_on: string;
  volume: number;
  concentration: number;
  total_count: number;
  motility: number;
  morphology: number;
  status: string;
  expired: boolean;
  comments: string;
  donor_semen_files?: ApiDonorSemenFile[];
  total?: string;
};

const DonorSemenBase = Record({
  collectedOn: "",
  volume: 0,
  concentration: 0,
  totalCount: 0,
  motility: 0,
  morphology: 0,
  status: "",
  expired: false,
  comments: "",
  donorSemenFiles: [] as DonorSemenFile[],
  total: "",
});

export class DonorSemen extends DonorSemenBase {
  constructor(apiDonorSemen?: ApiDonorSemen) {
    super({
      collectedOn: apiDonorSemen?.collected_on,
      volume: apiDonorSemen?.volume,
      concentration: apiDonorSemen?.concentration,
      totalCount: apiDonorSemen?.total_count,
      motility: apiDonorSemen?.motility,
      morphology: apiDonorSemen?.morphology,
      status: apiDonorSemen?.status,
      expired: apiDonorSemen?.expired,
      comments: apiDonorSemen?.comments,
      donorSemenFiles: (apiDonorSemen?.donor_semen_files || []).map((file) => new DonorSemenFile(file)),
      total: apiDonorSemen?.total || "",
    })
  }

  get certified(): boolean {
    if (this.status !== 'CERTIFIED') { return false }

    return this.inOneYear;
  }

  get inOneYear(): boolean {
    if (this.collectedOn === '') { return false }

    const collectedDate = new Date(this.collectedOn + 'T00:00:00');
    const today = new Date();
    const lastYear = new Date(today.getFullYear()-1, today.getMonth(), today.getDate());
    return collectedDate > lastYear;
  }

  get in6Month(): boolean {
    if (this.collectedOn === '') { return false }

    const collectedDate = new Date(this.collectedOn + 'T00:00:00');
    const today = new Date();
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    return collectedDate > sixMonthsAgo;
  }

  get validTotal(): string {
    return this.certified ? this.total : '';
  }

  readonly fileText: string = this.donorSemenFiles.length > 0 ? '添付済み' : '未添付';
  readonly fileAlert: boolean = this.donorSemenFiles.length === 0;

  get statusText(): string {
    switch (this.status) {
      case 'DRAFT':
        return '未提出';
      case 'SUBMITTED':
        return '確認中';
      case 'REMANDED':
        return '要確認';
      case 'CERTIFIED':
        if (!this.inOneYear) {
          return '期限切れ';
        } else {
          return '登録済';
        }
      default:
        return '未提出';
    }
  }
  readonly statusAlert: boolean = !this.certified && this.status !== 'SUBMITTED';

  readonly collectedOnText: string = this.collectedOn && this.collectedOn.replace(/-/g, '/');

  async updateTotalFromApi(): Promise<DonorSemen> {
    if (!this.volume || !this.concentration || !this.totalCount) {
       console.warn("Insufficient data (volume, concentration, or totalCount is 0) to calculate total.");
      return this.set('total', "") as DonorSemen;
    }

    let params = 'volume=' + this.volume;
    params += '&concentration=' + this.concentration;
    params += '&total_count=' + this.totalCount;
    params += '&motility=' + (this.motility || "");
    params += '&morphology=' + (this.morphology || "");

    const url = `${ process.env.REACT_APP_API_HOST }/api/v1/donor_semens_total?${ params }`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        return this.set('total', "") as DonorSemen;
      }

      const res = await response.json();

      const newTotal = res?.data?.toString() || "";

      return this.set('total', newTotal) as DonorSemen;

    } catch (error) {
      console.error("Failed to fetch total from API:", error);
      return this.set('total', "") as DonorSemen;
    }
  }

  isValidForRegistration(): boolean {
    const requiredFilled = !!this.collectedOn
        && !!this.volume
        && !!this.concentration
        && !!this.totalCount
        && !!this.donorSemenFiles
        && this.donorSemenFiles.length > 0;

    return this.status === 'DRAFT' && requiredFilled && this.in6Month;
  }
}
